import { RequestFormEndpoints } from '../types/endpoint.type'
import {
  IDevice,
  IStationClass,
  IEmissionType,
  IDeviceAntenna,
  INumberOfStations,
  IRadarTunabilityTypes,
  IRequestEquipment,
  IRequestStationClass,
  IEmissionDesignatorSimpleType,
  IRequestDeviceAntenna,
} from '../types/equipment.type'
import { IResponse } from '../types/response.type'
import { axiosInstance } from './axios.service'

export const getAllEquipments = async (id: number): Promise<IResponse<IRequestEquipment[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.REQUEST_EQUIPMENTS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createExerciseEquipment = async (id: number): Promise<IResponse<IRequestEquipment>> => {
  try {
    const data = await axiosInstance.post(RequestFormEndpoints.REQUEST_EQUIPMENTS, {
      exerciseRequestId: id,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

// Fields the updateRequestEquipmentValidator will accept on PUT — must match
// server/validations/request-form.validation.ts:updateRequestEquipmentValidator.
// IRequestEquipment contains server-managed fields (id, created_at, joined
// names) that joi rejects, so pick only the validator-allowed keys.
const REQUEST_EQUIPMENT_FIELDS: (keyof IRequestEquipment)[] = [
  'request_frequency_group_id',
  'equipment_name_id',
  'tuning_range',
  'tuning_increments',
  'number_of_frequencies',
  'number_of_stations_id',
  'model_number',
  'radar_tunability_type_id',
  'radar_pulse_duration',
  'radar_pulse_repetition_rate',
  'radar_intermediate_frequency',
  'radar_intermediate_frequency_indicator',
  'radar_sidelobe_suppression',
  'radar_power',
  'is_radar_chars',
  'receiving_unit_tier_id',
  'receiving_operating_unit_other',
  'send_receive',
  'f_343',
  'f_501_irac',
  'f_501_sps',
  'device_id',
]

export const updateEquipmentById = async (
  id: number,
  params: IRequestEquipment,
): Promise<IResponse<IRequestEquipment>> => {
  const body: Partial<IRequestEquipment> = {}
  for (const k of REQUEST_EQUIPMENT_FIELDS) if (k in params) (body as any)[k] = params[k]
  try {
    const data = await axiosInstance.put(`${RequestFormEndpoints.REQUEST_EQUIPMENTS}/${id}`, body)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteEquipmentById = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${RequestFormEndpoints.REQUEST_EQUIPMENTS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllDevices = async (): Promise<IResponse<IDevice[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_DEVICES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllNumberOfStations = async (): Promise<IResponse<INumberOfStations[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_NUMBER_OF_STATIONS)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllRadarTunabilityTypes = async (): Promise<IResponse<IRadarTunabilityTypes[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_RADAR_TUNABILITY_TYPES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}
export const getAllStationClassDescriptions = async (): Promise<IResponse<IStationClass[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.STATION_CLASS_DESCRIPTION)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllEmissionDesignatorSimpleTypes = async (): Promise<IResponse<IEmissionDesignatorSimpleType[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.EMISSION_DESIGNATOR_SIMPLE_TYPES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllEmissionTypes = async (): Promise<IResponse<IEmissionType[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.EMISSION_TYPES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllStationClasses = async (): Promise<IResponse<IStationClass[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.STATION_CLASS)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllRequestStationClasses = async (id: number): Promise<IResponse<IRequestStationClass[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.REQUEST_STATION_CLASS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createRequestStationClass = async (id: number): Promise<IResponse<IRequestStationClass>> => {
  try {
    const data = await axiosInstance.post(RequestFormEndpoints.REQUEST_STATION_CLASS, {
      deviceId: id,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

// Mirror updateRequestStationClassValidator's allow-list. IRequestStationClass
// also has id / created_at / request_equipment_id, which joi rejects.
const REQUEST_STATION_CLASS_FIELDS: (keyof IRequestStationClass)[] = [
  'station_class_id',
  'power',
  'power_units',
  'flight_level',
  'modulation_id',
  'traffic_id',
  'is_traffic_encrypted',
  'station_class_type',
  'antenna_elevation',
  'antenna_radius',
  'antenna_feed_point_height',
  'emission_type_id',
  'receiving_unit_tier_id',
  'operating_unit_other',
  'emission_designator_simple_type_id',
  'emission_designator_id',
]

export const updateRequestStationClassById = async (
  id: number,
  params: IRequestStationClass,
): Promise<IResponse<IRequestStationClass>> => {
  const body: Partial<IRequestStationClass> = {}
  for (const k of REQUEST_STATION_CLASS_FIELDS) if (k in params) (body as any)[k] = params[k]
  try {
    const resp = await axiosInstance.put(`${RequestFormEndpoints.REQUEST_STATION_CLASS}/${id}`, body)

    return resp.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteRequestStationClassById = async (id: number): Promise<IResponse<null>> => {
  try {
    const resp = await axiosInstance.delete(`${RequestFormEndpoints.REQUEST_STATION_CLASS}/${id}`)

    return resp.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllRequestDeviceAntennas = async (id: number): Promise<IResponse<IRequestDeviceAntenna[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.REQUEST_DEVICE_ANTENNA}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createRequestDeviceAntenna = async (equipmentId: number): Promise<IResponse<IRequestDeviceAntenna>> => {
  try {
    const data = await axiosInstance.post(RequestFormEndpoints.REQUEST_DEVICE_ANTENNA, { equipmentId })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

// Mirror updateRequestDeviceAntennaValidator's allow-list.
const REQUEST_DEVICE_ANTENNA_FIELDS: (keyof IRequestDeviceAntenna)[] = ['antenna_structure_height', 'device_antenna_id']

export const updateRequestDeviceAntennaById = async (
  id: number,
  params: IRequestDeviceAntenna,
): Promise<IResponse<IRequestDeviceAntenna>> => {
  const body: Partial<IRequestDeviceAntenna> = {}
  for (const k of REQUEST_DEVICE_ANTENNA_FIELDS) if (k in params) (body as any)[k] = params[k]
  try {
    const data = await axiosInstance.put(`${RequestFormEndpoints.REQUEST_DEVICE_ANTENNA}/${id}`, body)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteRequestDeviceAntennaById = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${RequestFormEndpoints.REQUEST_DEVICE_ANTENNA}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getALlDeviceAntennas = async (id: number): Promise<IResponse<IDeviceAntenna[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.GET_DEVICE_ANTENNAS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}
